/**
 * Signatures — send documents out, and see what came back.
 *
 * Everything here was reachable by API and by nothing else. An administrator
 * wanting to send an onboarding pack had to construct a POST by hand, which
 * means in practice it did not happen.
 *
 * The screen is built around the two questions an administrator actually has:
 * what do I need to send, and who has not signed. Sending is a packet by
 * default — onboarding, compliance, a POA with the forms it depends on — with
 * a counter-signer as a first-class field rather than an afterthought,
 * because for eMerge documents the employer half of the page is the norm.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PenLine, FileText, Plus, X, CheckCircle2, Clock, Loader2, ShieldCheck, Users, Upload,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api, { apiUpload } from '../../services/api';

interface Doc {
  id: string;
  title: string;
  original_filename: string | null;
  category: string | null;
  file_size_bytes: number | null;
  is_signed_copy: boolean;
}

interface Request {
  id: string;
  signer_email: string;
  signer_name: string | null;
  status: string;
  template_name: string | null;
  is_counter_signer: boolean;
  signing_order: number;
  sent_at: string | null;
  completed_at: string | null;
  envelope_title: string | null;
  envelope_status: string | null;
  routing_mode: string | null;
  signed_copy_id: string | null;
}

interface Envelope {
  id: string;
  title: string;
  status: string;
  routing_mode: string;
  recipients: number;
  signed: number;
  counter_signers: number;
  awaiting_counter_signature: boolean;
  created_at: string;
}

interface Person { email: string; name: string }
interface Folder { collection: string; documents: number }

const kb = (b: number | null) => (b ? `${Math.round(b / 1024).toLocaleString()} KB` : '—');
const when = (iso: string | null) =>
  (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');

const STATUS_STYLE: Record<string, string> = {
  signed: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  completed: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  sent: 'text-blue-700 bg-blue-50 border-blue-200',
  viewed: 'text-blue-700 bg-blue-50 border-blue-200',
  pending: 'text-gray-600 bg-gray-50 border-gray-200',
  declined: 'text-red-700 bg-red-50 border-red-200',
};

export default function SignatureConsole() {
  const [docs, setDocs] = useState<Doc[]>([]);
  const [requests, setRequests] = useState<Request[]>([]);
  const [envelopes, setEnvelopes] = useState<Envelope[]>([]);
  const [loading, setLoading] = useState(true);

  const [composing, setComposing] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [people, setPeople] = useState<Person[]>([{ email: '', name: '' }]);
  const [counterSigner, setCounterSigner] = useState<Person>({ email: '', name: '' });
  const [wantsCounterSignature, setWantsCounterSignature] = useState(true);
  const [title, setTitle] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [destination, setDestination] = useState('');
  const fileInput = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // `api.get` is `apiRequest.bind(null, 'GET')`, so its arguments are
      // (url, body, config) and it returns the RESPONSE BODY — not an axios
      // response. Two consequences, both of which this screen got wrong and
      // neither of which announces itself:
      //
      //   - query options belong in the third argument. Passed second they
      //     become a GET request body, which axios drops on the floor.
      //   - the body is already `{ success, data }`, so reading `.data.data`
      //     is one level too deep and yields undefined.
      //
      // And `apiRequest` catches its own errors and returns `{success:false}`
      // rather than throwing, so a `?? []` fallback turns every one of those
      // mistakes into an empty screen with no error — which is exactly what
      // this was: five documents in the tenant, a completed envelope, and a
      // page reporting nothing at all.
      const [d, r, e, f] = (await Promise.all([
        api.get('/admin/documents', undefined, { params: { limit: 200 } }),
        api.get('/admin/policies/signature-requests'),
        api.get('/admin/policies/envelopes', undefined, { params: { limit: 50 } }),
        api.get('/admin/documents/collections'),
      ])) as any[];

      // Folders are organisation, not correctness: a failure to load them
      // must not stop the screen that sends documents for signature.
      const failed = [d, r, e].find((x) => x?.success === false);
      if (failed) throw new Error(failed.error || failed.message || 'Request failed');

      // Signed copies are outputs, not things to send. They are listed under
      // the request that produced them instead.
      setDocs(((d?.documents ?? []) as Doc[]).filter((x) => !x.is_signed_copy));
      setRequests((r?.data ?? []) as Request[]);
      setEnvelopes((e?.data?.envelopes ?? []) as Envelope[]);
      setFolders((f?.data?.collections ?? []) as Folder[]);
    } catch (err: any) {
      toast.error(err?.message ? `Could not load signatures — ${err.message}` : 'Could not load signatures.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleDoc = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  /**
   * Upload a document to send.
   *
   * The file goes to S3 — bucket `emerge-documents-<account>`, key
   * `emerge/templates/<random>`, SSE-AES256, the bucket's four public-access
   * blocks on and versioning enabled. Postgres stores the pointer and the
   * metadata, never the bytes, which is why the signing engine can hash the
   * exact object the signer was shown.
   *
   * `folder: templates` is deliberate: this is a source document that signed
   * copies derive from, and it keeps it out of `signed/`, where a copy that
   * already carries a signature would be re-sendable.
   */
  const uploadDoc = async (file: File) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('folder', 'templates');
      form.append('category', 'signature');
      // apiUpload, not api.post. The shared client defaults to a JSON
      // Content-Type, and axios reads that header before it looks at the body:
      // FormData posted through `api.post` is silently serialised to JSON, no
      // file is sent, and the route answers 400. apiUpload overrides the header
      // so the browser writes a real multipart body.
      const res = (await apiUpload<Doc>('/admin/documents/upload', form)) as any;
      if (res?.success === false) {
        toast.error(res.error || res.message || 'Upload failed.');
        return;
      }
      const doc = res?.data as Doc | undefined;
      if (!doc?.id) { toast.error('Upload returned no document.'); return; }
      toast.success(`Uploaded ${doc.title || file.name}`);
      await load();
      // Ticked automatically: uploading a document from the send form is
      // already a statement that you intend to send it.
      setSelected((s) => (s.includes(doc.id) ? s : [...s, doc.id]));
    } catch (err: any) {
      toast.error(err?.message || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const send = async () => {
    const recipients = people
      .map((p) => ({ email: p.email.trim(), name: p.name.trim() || undefined }))
      .filter((p) => p.email);

    if (!selected.length) { toast.error('Choose at least one document.'); return; }
    if (!recipients.length) { toast.error('Add at least one recipient.'); return; }

    const cs = counterSigner.email.trim();
    if (wantsCounterSignature && !cs) {
      toast.error('Add a counter-signer, or turn counter-signature off.');
      return;
    }

    setSending(true);
    try {
      const res = (await api.post('/admin/policies/send', {
        documentIds: selected,
        recipients,
        title: title.trim() || undefined,
        collection: destination.trim() || undefined,
        counterSigner: wantsCounterSignature
          ? { email: cs, name: counterSigner.name.trim() || undefined }
          : undefined,
      })) as any;
      // A rejected send comes back as `{success:false}`, not as a throw, so
      // without this check a refusal reported itself as "Sent to 0".
      if (res?.success === false) {
        toast.error(res.error || res.message || 'Could not send.');
        return;
      }
      const data = res?.data;
      // Reported from what actually left, not from what was asked for.
      const parts = [`Sent to ${data?.sent ?? 0}`];
      if (data?.skipped) parts.push(`${data.skipped} already had it`);
      if (data?.undelivered) parts.push(`${data.undelivered} did not deliver`);
      toast.success(parts.join(' · '));
      setComposing(false);
      setSelected([]);
      setPeople([{ email: '', name: '' }]);
      setCounterSigner({ email: '', name: '' });
      setTitle('');
      setDestination('');
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not send.');
    } finally {
      setSending(false);
    }
  };

  const outstanding = useMemo(
    () => requests.filter((r) => !['signed', 'completed', 'declined'].includes(r.status)),
    [requests]
  );
  const awaitingCounter = useMemo(
    () => envelopes.filter((e) => e.awaiting_counter_signature),
    [envelopes]
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <PenLine className="w-6 h-6 text-emerald-600" />
            Signatures
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Send one document or a packet. Everyone gets a single link covering
            everything they need to sign, and each document keeps its own record.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setComposing((c) => !c)}
          className="shrink-0 inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded bg-emerald-700 text-white hover:bg-emerald-800"
        >
          <Plus className="w-4 h-4" /> Send for signature
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        {[
          ['Awaiting signature', outstanding.length, <Clock className="w-4 h-4" key="c" />],
          ['Awaiting counter-signature', awaitingCounter.length, <ShieldCheck className="w-4 h-4" key="s" />],
          ['Completed', envelopes.filter((e) => e.status === 'completed').length, <CheckCircle2 className="w-4 h-4" key="k" />],
        ].map(([label, n, icon]) => (
          <div key={String(label)} className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="text-2xl font-semibold tabular-nums text-gray-900">{n as number}</div>
            <div className="text-xs uppercase tracking-wide text-gray-500 mt-1 flex items-center gap-1">
              {icon as React.ReactNode}{label as string}
            </div>
          </div>
        ))}
      </div>

      {composing && (
        <div className="border border-gray-200 rounded-lg bg-white p-5 mb-8">
          <h2 className="font-semibold text-gray-900 mb-4">Send for signature</h2>

          <div className="flex items-center justify-between gap-4 mb-2">
            <label className="block text-xs font-medium text-gray-700">
              Documents — pick every one that needs a signature
            </label>
            <input
              ref={fileInput}
              type="file"
              accept=".pdf,application/pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadDoc(f);
              }}
            />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              className="shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              {uploading
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
                : <><Upload className="w-3.5 h-3.5" /> Upload a PDF</>}
            </button>
          </div>
          <div className="border border-gray-200 rounded max-h-60 overflow-y-auto mb-1 divide-y divide-gray-100">
            {docs.length === 0 && (
              <p className="text-sm text-gray-500 p-3">
                Nothing to send yet — upload a PDF, or add one under Content ›
                Documents.
              </p>
            )}
            {docs.map((d) => (
              <label key={d.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.includes(d.id)}
                  onChange={() => toggleDoc(d.id)}
                  className="rounded border-gray-300"
                />
                <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="text-sm text-gray-900 truncate flex-1">{d.title}</span>
                {d.category && (
                  <span className="hidden sm:inline text-xs text-gray-400 shrink-0">{d.category}</span>
                )}
                <span className="text-xs text-gray-400 shrink-0 tabular-nums w-16 text-right">{kb(d.file_size_bytes)}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-500 mb-4">
            {selected.length === 0 && 'Nothing selected.'}
            {selected.length === 1 && '1 document — one signing link.'}
            {selected.length > 1 && `${selected.length} documents — one link, reviewed and signed in one sitting.`}
          </p>

          <label className="block text-xs font-medium text-gray-700 mb-2">
            <Users className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Recipients
          </label>
          {people.map((p, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input
                value={p.email}
                onChange={(e) => setPeople((ps) => ps.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))}
                placeholder="name@emergesc.com"
                className="flex-1 text-sm border border-gray-300 rounded px-2 py-1.5"
              />
              <input
                value={p.name}
                onChange={(e) => setPeople((ps) => ps.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                placeholder="Full name (optional)"
                className="w-56 text-sm border border-gray-300 rounded px-2 py-1.5"
              />
              {people.length > 1 && (
                <button
                  type="button"
                  onClick={() => setPeople((ps) => ps.filter((_, j) => j !== i))}
                  className="px-2 text-gray-400 hover:text-gray-700"
                  aria-label="Remove recipient"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => setPeople((ps) => [...ps, { email: '', name: '' }])}
            className="text-xs text-emerald-700 hover:underline mb-4"
          >
            + Add another recipient
          </button>

          <div className="border-t border-gray-100 pt-4 mb-4">
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={wantsCounterSignature}
                onChange={(e) => setWantsCounterSignature(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm font-medium text-gray-900">
                Requires a counter-signature
              </span>
            </label>
            <p className="text-xs text-gray-500 mb-2">
              The counter-signer is asked only after the recipient has signed, and
              signs the recipient's copy — so the employer block is filled and the
              employee's signature is kept.
            </p>
            {wantsCounterSignature && (
              <div className="flex gap-2">
                <input
                  value={counterSigner.email}
                  onChange={(e) => setCounterSigner((c) => ({ ...c, email: e.target.value }))}
                  placeholder="counter-signer@emergesc.com"
                  className="flex-1 text-sm border border-gray-300 rounded px-2 py-1.5"
                />
                <input
                  value={counterSigner.name}
                  onChange={(e) => setCounterSigner((c) => ({ ...c, name: e.target.value }))}
                  placeholder="Full name (optional)"
                  className="w-56 text-sm border border-gray-300 rounded px-2 py-1.5"
                />
              </div>
            )}
          </div>

          <label className="block text-xs font-medium text-gray-700 mb-1">
            Folder for the executed copies
          </label>
          <p className="text-xs text-gray-500 mb-2">
            Every signed copy lands here. One policy sent to twenty people
            produces forty documents, and unfiled they become the scroll this
            exists to prevent. Type a new name, or pick one already in use.
          </p>
          <input
            list="esign-folders"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder="e.g. 2026 Card Policies"
            className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mb-1"
          />
          <datalist id="esign-folders">
            {folders.map((f) => <option key={f.collection} value={f.collection} />)}
          </datalist>
          <p className="text-xs text-gray-400 mb-4">
            {destination.trim()
              ? (folders.some((f) => f.collection === destination.trim())
                  ? 'Existing folder.'
                  : 'This will create a new folder.')
              : 'Left empty, the copies stay unfiled.'}
          </p>

          <label className="block text-xs font-medium text-gray-700 mb-1">
            Title shown to the recipient (optional)
          </label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={selected.length > 1 ? `${selected.length} documents for signature` : 'Defaults to the document title'}
            className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 mb-5"
          />

          <div className="flex gap-2">
            <button
              type="button" onClick={send} disabled={sending}
              className="text-sm px-5 py-2 rounded bg-emerald-700 text-white disabled:opacity-40 hover:bg-emerald-800"
            >
              {sending ? 'Sending…' : 'Send'}
            </button>
            <button
              type="button" onClick={() => setComposing(false)}
              className="text-sm px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <section>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700 mb-3">
            Signature requests
          </h2>
          {requests.length === 0 ? (
            <div className="text-center py-12 text-gray-500 border border-dashed border-gray-300 rounded-lg">
              Nothing has been sent yet.
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white divide-y divide-gray-100">
              {requests.map((r) => (
                <div key={r.id} className="flex items-center gap-4 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-gray-900 truncate">
                      {r.template_name || r.envelope_title || 'Document'}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {r.signer_name ? `${r.signer_name} · ` : ''}{r.signer_email}
                    </div>
                  </div>

                  {r.is_counter_signer && (
                    <span className="hidden sm:inline text-xs px-2 py-0.5 rounded border border-violet-200 bg-violet-50 text-violet-700 shrink-0">
                      counter-signer
                    </span>
                  )}

                  <span className={`text-xs px-2 py-0.5 rounded border shrink-0 ${STATUS_STYLE[r.status] || STATUS_STYLE.pending}`}>
                    {r.status}
                  </span>

                  <div className="hidden md:block w-16 shrink-0 text-xs text-gray-500 tabular-nums text-right">
                    {when(r.completed_at || r.sent_at)}
                  </div>

                  <div className="w-24 shrink-0 text-right">
                    {r.signed_copy_id ? (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const res = (await api.get(`/admin/documents/${r.signed_copy_id}/download`)) as any;
                            const url = res?.data?.url;
                            if (url) window.open(url, '_blank', 'noopener');
                            else toast.error('No download link was returned.');
                          } catch {
                            toast.error('Could not fetch the signed copy.');
                          }
                        }}
                        className="text-xs text-emerald-700 hover:underline"
                      >
                        Signed copy
                      </button>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
